import { lazy, useContext } from "react";
import { Navigate, Route, Routes, useParams } from "react-router-dom";

import { AuthContext } from "../hooks/AuthContext";
import AppLayout from "../layouts/AppLayout";
import Loading from "../components/Loading";
import AdminRoute from "./AdminRoute";
import InternalRoute from "./InternalRoute";
import ProtectedRoute from "./ProtectedRoute";

const SignIn = lazy(() => import("../components/SignIn"));
const Dashboard = lazy(() => import("../components/Dashboard"));
const SearchNumber = lazy(() => import("../components/SearchNumber"));
const SearchTollFree = lazy(() => import("../components/SearchTollFree"));
const BrightlinkOrderPage = lazy(() => import("../components/BrightlinkOrderPage"));
const ManageUsers = lazy(() => import("../components/ManageUsers"));
const DomainLogs = lazy(() => import("../components/DomainLogs"));
const Customers = lazy(() => import("../components/Customers"));
const CustomerList = lazy(() => import("../components/CustomerList"));
const CustomerHomePage = lazy(() => import("../components/CustomerHomePage"));
const BulkCallForwardPage = lazy(() => import("../components/BulkCallForwardPage"));
const NotFound = lazy(() => import("../components/NotFound"));

export type BulkCallForwardPageType = "create" | "activate-deactivate";

/** Reads `:customerId` from the URL and renders the customer detail page. */
function CustomerHomeRoute() {
  const { customerId } = useParams();
  return <CustomerHomePage customerId={Number(customerId)} />;
}

/**
 * The customer-scoped inventory list. Customers only ever see the accounts
 * attached to their own user record, which is what keeps them from browsing
 * to another customer's page.
 */
function OwnCustomerListRoute() {
  const { user } = useContext(AuthContext);
  if (!user?.customers) return <Navigate to="/login" replace />;
  return <CustomerList customers={user.customers} />;
}

function OwnCustomerHomeRoute() {
  const { user } = useContext(AuthContext);
  const { customerId } = useParams();
  if (!user?.customers) return <Navigate to="/login" replace />;
  return <CustomerHomePage customerId={Number(customerId)} />;
}

function BulkCallForwardRoute({ isAdmin }: { isAdmin?: boolean }) {
  const { customerId, pageType } = useParams();
  return (
    <BulkCallForwardPage
      customerId={Number(customerId)}
      pageType={pageType as BulkCallForwardPageType}
      isAdmin={isAdmin}
    />
  );
}

function RootRedirect() {
  const { isAuthenticated, isLoading } = useContext(AuthContext);
  if (isLoading) return <Loading />;
  return <Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />;
}

export default function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/login" element={<SignIn />} />

      {/* Everything below renders inside the persistent app shell. */}
      <Route
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<Dashboard />} />

        {/*
          The search and order pages are hidden from the nav for non-staff but
          have never been role-guarded at the route level. That is preserved
          here rather than silently tightened.
        */}
        <Route path="/search/number" element={<SearchNumber />} />
        <Route path="/search/tollfree" element={<SearchTollFree />} />
        <Route path="/order/brightlink" element={<BrightlinkOrderPage />} />

        <Route
          path="/admin/users"
          element={
            <AdminRoute>
              <ManageUsers />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/domain-logs"
          element={
            <AdminRoute>
              <DomainLogs />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/customers"
          element={
            <InternalRoute>
              <Customers />
            </InternalRoute>
          }
        />
        <Route
          path="/admin/customers/:customerId"
          element={
            <InternalRoute>
              <CustomerHomeRoute />
            </InternalRoute>
          }
        />
        <Route
          path="/admin/customers/:customerId/bulk-call-forward/:pageType"
          element={<BulkCallForwardRoute isAdmin />}
        />

        <Route path="/profile/customers" element={<OwnCustomerListRoute />} />
        <Route path="/profile/customers/:customerId" element={<OwnCustomerHomeRoute />} />
        <Route
          path="/profile/customers/:customerId/bulk-call-forward/:pageType"
          element={<BulkCallForwardRoute />}
        />

        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}
